import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { buildReportPdfBlob } from "@/lib/reportPdf";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { X, Loader2, FileText, Share2, Copy, ArrowRightLeft, CalendarClock, CheckCircle2 } from "lucide-react";

// Full-screen: AI sorts the report's items into per-trade buckets. Each bucket
// spawns a child report + live link; Send it on with the phone's native share
// sheet, or download that trade's PDF.
export function PunchDispatch({ reportId, onClose, jobName }) {
  const [loading, setLoading] = useState(true);
  const [buckets, setBuckets] = useState([]);
  const [sending, setSending] = useState(null);
  const [deadline, setDeadline] = useState("");
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.post(`/reports/${reportId}/sort-by-trade`);
        setBuckets((data.buckets || []).map((b) => ({
          ...b,
          sent: !!b.dispatched,
          childId: b.dispatched?.child_report_id || null,
          shareUrl: toLocalShare(b.dispatched?.share_url),
          views: b.dispatched?.views || 0,
        })));
      } catch { toast.error("Could not sort items"); }
      finally { setLoading(false); }
    })();
  }, [reportId]);

  // Rewrite a backend-built share URL to the CURRENT app origin so a stale
  // server APP_URL can never point the worker at a dead host ("Link not found").
  const toLocalShare = (u) => {
    if (!u) return null;
    try { const p = new URL(u); return `${window.location.origin}${p.pathname}${p.search}`; }
    catch { return u; }
  };

  // Lazily spawn the child report (+ live link) the first time a bucket is
  // shared / PDF'd / copied — no separate "create link" step.
  const ensure = async (ti) => {
    const b = buckets[ti];
    if (b.childId) return b;
    const { data } = await api.post(`/reports/${reportId}/dispatch`, {
      trade: b.trade, item_ids: b.items.map((it) => it.id), vendor_id: null, due_date: deadline || null,
    });
    const updated = { ...b, sent: true, childId: data.child_report_id, shareUrl: toLocalShare(data.share_url) };
    setBuckets((bs) => bs.map((x, i) => (i === ti ? updated : x)));
    return updated;
  };

  // Reassign a single item to another (not-yet-dispatched) trade bucket.
  const moveItem = (fromTrade, itemId, toTrade) => {
    setBuckets((bs) => {
      let moved = null;
      const stripped = bs.map((bk) => {
        if (bk.trade !== fromTrade) return bk;
        moved = bk.items.find((x) => x.id === itemId) || null;
        return { ...bk, items: bk.items.filter((x) => x.id !== itemId) };
      });
      const placed = stripped.map((bk) => (bk.trade === toTrade && moved ? { ...bk, items: [...bk.items, moved] } : bk));
      return placed.filter((bk) => bk.childId || bk.items.length > 0);
    });
    toast.success(`Moved to ${toTrade}`);
  };

  const fmtDeadline = (d) => new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const bodyText = (b) => {
    const head = jobName ? `Here's the ${b.trade} punch list for ${jobName}.` : `Here's the ${b.trade} punch list.`;
    const due = deadline ? ` Please complete by ${fmtDeadline(deadline)}.` : "";
    return `${head}${due}\n\nOpen the link and for each item, snap a quick proof photo of the finished work right there. Hit "Send proof back" when you're done and I'll get it instantly.\n\n📸 A photo on each item is what closes it out.\n\nForward this to anyone on your crew helping out.\n\n${b.shareUrl}`;
  };
  const nativeShare = async (text) => {
    if (navigator.share) {
      try { await navigator.share({ text }); return; }
      catch (e) { if (e && e.name === "AbortError") return; /* fall through on real errors */ }
    }
    // Never use sms: (iOS shows an "Open in Messages?" prompt) — copy instead.
    try { await navigator.clipboard.writeText(text); toast.success("Message copied — paste it into any text or email"); }
    catch { toast.error("Couldn't open share"); }
  };
  const doShare = async (ti) => {
    const b = buckets[ti];
    // Already tracked: share synchronously so iOS keeps the tap gesture and opens the native sheet.
    if (b.childId && b.shareUrl) { await nativeShare(bodyText(b)); return; }
    setSending(ti);
    try {
      const nb = await ensure(ti);
      await nativeShare(bodyText(nb));
    } catch { toast.error("Send failed"); }
    finally { setSending(null); }
  };
  const sharePdf = async (ti) => {
    setSending(ti);
    try {
      const b = await ensure(ti);
      if (!b.childId) throw new Error("no child");
      const blob = await buildReportPdfBlob(b.childId);
      const fname = `${(jobName || "punch-list").replace(/[^\w.-]+/g, "_")}-${b.trade}.pdf`;
      const file = new File([blob], fname, { type: "application/pdf" });
      // Prefer the phone's native share sheet with the actual PDF file attached.
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: fname });
      } else {
        // Fallback (desktop / no file-share support): download the PDF.
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = fname; a.rel = "noopener";
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 15000);
        toast.success("PDF downloaded");
      }
    } catch (e) {
      if (!(e && e.name === "AbortError")) toast.error("Couldn't build the PDF");
    } finally { setSending(null); }
  };
  const copyLink = async (ti) => {
    try { const b = await ensure(ti); await navigator.clipboard.writeText(b.shareUrl); toast.success("Link copied"); }
    catch { toast.error("Couldn't copy the link"); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-background" data-testid="punch-dispatch">
      <div className="flex items-center justify-between border-b border-border px-4 pb-3" style={{ paddingTop: "calc(env(safe-area-inset-top) + 0.75rem)" }}>
        <span className="font-heading font-bold">Sort &amp; Send by trade</span>
        <button onClick={onClose} data-testid="dispatch-close" aria-label="Close"><X className="h-6 w-6" /></button>
      </div>

      {loading ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="dispatch-loading">
          <Loader2 className="h-7 w-7 animate-spin text-emerald-500" /> Sorting items by trade…
        </div>
      ) : (
        <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)" }}>
          <div className="rounded-2xl border border-border bg-card p-4" data-testid="dispatch-deadline">
            <label htmlFor="dispatch-deadline-input" className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <CalendarClock className="h-4 w-4 text-emerald-500" /> Deadline
              <span className="ml-auto text-xs font-normal text-muted-foreground">optional</span>
            </label>
            <div className="mt-2 flex items-center gap-2">
              <input
                id="dispatch-deadline-input"
                type="date"
                data-testid="dispatch-deadline-input"
                value={deadline}
                min={today}
                onChange={(e) => setDeadline(e.target.value)}
                className="h-11 flex-1 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-emerald-500 focus:outline-none"
              />
              {deadline && (
                <button data-testid="dispatch-deadline-clear" onClick={() => setDeadline("")} className="rounded-lg px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground">Clear</button>
              )}
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">Set before sending — it's added to each message so trades know when it's due.</p>
          </div>
          {buckets.length === 0 && <p className="text-center text-sm text-muted-foreground">No items to sort yet.</p>}
          {buckets.map((b, ti) => (
            <div key={b.trade} className="rounded-2xl border border-border bg-card p-4" data-testid={`bucket-${b.trade}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="rounded-full bg-emerald-500/15 px-3 py-1 text-sm font-semibold text-emerald-400">{b.trade}</span>
                <div className="flex items-center gap-2">
                  {b.sent && (
                    <span data-testid={`bucket-tracked-${b.trade}`} className="inline-flex items-center gap-1 rounded-full bg-emerald-500 px-2 py-1 text-[11px] font-bold text-white">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Tracked{b.views ? ` · ${b.views} open${b.views === 1 ? "" : "s"}` : ""}
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground">{b.items.length} item{b.items.length === 1 ? "" : "s"}</span>
                </div>
              </div>
              <ul className="mt-3 space-y-1.5 text-sm">
                {b.items.map((it) => {
                  const targets = buckets.filter((x) => x.trade !== b.trade && !x.childId);
                  return (
                    <li key={it.id} className="flex items-center gap-2">
                      <span className="min-w-0 flex-1 truncate text-muted-foreground">• {it.title}</span>
                      {!b.childId && targets.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button data-testid={`move-item-${it.id}`} aria-label="Move to another trade" className="shrink-0 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                              <ArrowRightLeft className="h-3.5 w-3.5" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="z-[70]">
                            <DropdownMenuLabel className="text-xs text-muted-foreground">Move to…</DropdownMenuLabel>
                            {targets.map((t) => (
                              <DropdownMenuItem key={t.trade} data-testid={`move-to-${it.id}-${t.trade}`} onClick={() => moveItem(b.trade, it.id, t.trade)}>
                                {t.trade}
                              </DropdownMenuItem>
                            ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </li>
                  );
                })}
              </ul>
              <div className="mt-3">
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => doShare(ti)} disabled={sending === ti} data-testid={`bucket-share-${b.trade}`} className="h-11 gap-1 bg-emerald-500 hover:bg-emerald-600">
                    {sending === ti ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Share2 className="h-4 w-4" /> {b.sent ? "Re-share" : "Share"}</>}
                  </Button>
                  <Button variant="outline" onClick={() => sharePdf(ti)} disabled={sending === ti} data-testid={`bucket-pdf-${b.trade}`} className="h-11 gap-1"><FileText className="h-4 w-4" /> PDF</Button>
                </div>
                <button onClick={() => copyLink(ti)} data-testid={`bucket-copy-${b.trade}`} className="mt-2 flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground">
                  <Copy className="h-3.5 w-3.5" /> Copy live link
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
